import { AlertTriangle } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import BooleanSelect from "@/components/onboarding/shared/BooleanSelect";
import { FieldTooltip } from "@/components/onboarding/shared/FieldTooltip";
import { ONBOARDING_TOOLTIPS } from "@/utils/onboardingTooltips";
import type { EntityData, SituatieServiciiUEData } from "@/types/entity";

type EUServicesSectionProps = {
  entityData: Partial<EntityData>;
  onFieldChange: (
    field: keyof SituatieServiciiUEData,
    value: boolean | null
  ) => void;
  onStatesChange: (value: string) => void;
  onAddressesChange: (value: string) => void;
  fieldErrors: Record<string, string>;
};

type BooleanSituatieField = Extract<
  keyof SituatieServiciiUEData,
  "furnizeaza_servicii_romania" | "furnizeaza_servicii_ue"
>;

type BooleanQuestion = {
  field: BooleanSituatieField;
  label: string;
  tooltip: string;
};

const QUESTIONS: BooleanQuestion[] = [
  {
    field: "furnizeaza_servicii_romania",
    label: "Furnizați servicii în România?",
    tooltip: ONBOARDING_TOOLTIPS.furnizeaza_servicii_romania,
  },
  {
    field: "furnizeaza_servicii_ue",
    label: "Furnizați servicii într-un alt stat membru al UE?",
    tooltip: ONBOARDING_TOOLTIPS.furnizeaza_servicii_ue,
  },
];

function FieldError({ error }: { error: string | undefined }) {
  if (!error) return null;
  return (
    <div className="flex items-center gap-2 mt-1 px-4">
      <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
      <span className="text-xs text-destructive">{error}</span>
    </div>
  );
}

function EUServicesSection({
  entityData,
  onFieldChange,
  onStatesChange,
  onAddressesChange,
  fieldErrors,
}: EUServicesSectionProps) {
  const data = entityData.situatie_servicii_ue;
  const showStatesField = data?.furnizeaza_servicii_ue === true;

  return (
    <div className="py-2 space-y-4">
      <div className="border border-ds-border-200 rounded-lg overflow-hidden divide-y divide-ds-border-100">
        {QUESTIONS.map(({ field, label, tooltip }) => (
          <div key={field}>
            <div className="flex items-center justify-between gap-4 px-4 py-3">
              <span className="text-xs md:text-sm text-ds-text-600 flex-1 flex items-center gap-1">
                {label}
                <FieldTooltip content={tooltip} />
              </span>
              <div className="w-32 shrink-0">
                <BooleanSelect
                  value={data?.[field] ?? null}
                  onChange={(val) => onFieldChange(field, val)}
                  variant="neutral"
                />
              </div>
            </div>
            <FieldError error={fieldErrors[`situatie_servicii_ue.${field}`]} />
          </div>
        ))}
      </div>

      {showStatesField && (
        <div className="border border-ds-border-200 rounded-lg p-4 space-y-3">
          <Label className="text-sm font-medium flex items-center gap-1">
            State membre UE în care furnizați servicii
            <FieldTooltip content={ONBOARDING_TOOLTIPS.state_membre_ue} />
          </Label>
          <Textarea
            placeholder="ex: Germania, Franța, Italia"
            value={data?.state_membre_ue ?? ""}
            onChange={(e) => onStatesChange(e.target.value)}
            className="min-h-[80px] text-sm"
          />
          <FieldError
            error={fieldErrors["situatie_servicii_ue.state_membre_ue"]}
          />
          <Label className="text-sm font-medium flex items-center gap-1 pt-2">
            Adresele sediilor din statele membre UE
          </Label>
          <Textarea
            placeholder="ex: Str. Example 1, Berlin, Germania"
            value={data?.adrese_ue ?? ""}
            onChange={(e) => onAddressesChange(e.target.value)}
            className="min-h-[80px] text-sm"
          />
          <FieldError error={fieldErrors["situatie_servicii_ue.adrese_ue"]} />
        </div>
      )}
    </div>
  );
}

export default EUServicesSection;
